import React, { useState, useEffect } from 'react';
import { Row, Col, notification, Space, Tag } from 'antd';
import TabPane from 'antd/es/tabs/TabPane';
import {
	XContainer,
	XButton,
	XThemeContainer,
	XThemeGradientButton,
	XThemeFilterCards,
	XThemeTable,
	XThemeInputText,
	XThemeFilterButton,
	XCustomTabsFilter,
} from '../../styles/GlobalThemeStyle';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/Header/Header';
import HeaderFile from '../../components/Header/HeaderFile';
import { getLogsApi, getLogsReset } from '../../actions/getLogsAction';
import { connect } from 'react-redux';
import moment from 'moment';

const ActivityLogs = (props) => {
	const { getLogsState, getLogs, getLogsReset } = props;
	const [activeTab, setActiveTab] = useState('case');
	const [searchValue, setSearchValue] = useState('');
	const [pagination, setPagination] = useState({
		current: 1,
		pageSize: 10,
		total: 0,
	});

	useEffect(() => {
		fetchLogs({
			current: 1,
			pageSize: 10,
		});
		return () => {
			getLogsReset();
		};
	}, [activeTab]);

	useEffect(() => {
		if (getLogsState.data?.pagination) {
			setPagination((prev) => ({
				...prev,
				total: getLogsState.data.pagination.total,
			}));
		}
	}, [getLogsState.data]);

	const fetchLogs = (params = {}) => {
		const queryParams = {
			page: params.current || pagination.current,
			limit: params.pageSize || pagination.pageSize,
			type: activeTab,
			sort_order: params.order === 'ascend' ? 1 : -1,
			...(params.search_value && { search_value: params.search_value }),
		};

		getLogs(queryParams);
	};

	const handleTableChange = (pagination, filters, sorter) => {
		fetchLogs({
			current: pagination.current,
			pageSize: pagination.pageSize,
			order: sorter.order,
		});
	};

	const handleTabChange = (key) => {
		setActiveTab(key);
		setSearchValue('');
		setPagination({
			current: 1,
			pageSize: 10,
			total: 0,
		});
	};

	const handleSearch = (value) => {
		if (!value.trim()) {
			return; // Don't make API call if search value is empty
		}
		setSearchValue(value);
		fetchLogs({
			current: 1,
			pageSize: pagination.pageSize,
			search_value: value.trim(),
		});
	};

	const handleSearchClear = () => {
		setSearchValue('');
		setPagination((prev) => ({
			...prev,
			current: 1,
			pageSize: 10,
		}));
		fetchLogs({
			current: 1,
			pageSize: 10,
			search_value: '',
		});
	};

	const columns = [
		{
			title: activeTab === 'case' ? 'Case ID' : 'Analysis ID',
			dataIndex: activeTab === 'case' ? 'case_id' : 'analysis_id',
			width: 200,
			align: 'center',
			render: (text, record) => (
				<Link to={`/logs/${record._id}`}>
					<div
						style={{
							whiteSpace: 'nowrap',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
						}}
					>
						{text || '-'}
					</div>
				</Link>
			),
		},
		{
			title: 'Total Logs',
			dataIndex: 'logs_count',
			width: 120,
			align: 'center',
			render: (count, record) => (
				<Link to={`/logs/${record._id}`}>
					<div>{count || 0}</div>
				</Link>
			),
		},
		{
			title: 'Last Log Updated on',
			dataIndex: 'last_updated',
			width: 150,
			align: 'center',
			sorter: true,
			render: (date) => (
				<div>{moment(date).format('DD-MM-YYYY HH:mm:ss')}</div>
			),
		},
		{
			title: 'Request ID',
			dataIndex: 'request_id',
			width: 200,
			align: 'center',
			render: (text) => (
				<div
					style={{
						whiteSpace: 'nowrap',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
					}}
				>
					{text || '-'}
				</div>
			),
		},
	];

	return (
		<>
			{props.displayHeader ? (
				<></>
			) : (
				<>
					<Col lg={24} xl={24} xs={0} md={0}>
						<Header headerTag="Logs" />
					</Col>
					<Col lg={0} xl={0} xs={24} md={24}>
						<HeaderFile activeNav="Logs" />
					</Col>
				</>
			)}
			<XThemeContainer fluid>
				<XCustomTabsFilter
					defaultActiveKey={activeTab}
					onChange={handleTabChange}
					type="card"
				>
					<TabPane key="case" tab="Cases Logs">
						<XThemeFilterCards className="smallTopBottomPadding mb-3">
							<Row
								gutter={[15, 15]}
								className="align-items-center"
							>
								<Col
									xs={24}
									sm={16}
									md={12}
									lg={12}
									className="mb-3 mb-md-0"
								>
									<Space>
										<XThemeInputText
											size="large"
											allowClear
											placeholder={`Search by ${
												activeTab === 'case'
													? 'Case ID'
													: 'Analysis ID'
											}`}
											value={searchValue}
											onChange={(e) => {
												setSearchValue(e.target.value);
												if (!e.target.value) {
													handleSearchClear();
												}
											}}
											onPressEnter={() =>
												handleSearch(searchValue)
											}
										/>
										<XThemeFilterButton
											onClick={() =>
												handleSearch(searchValue)
											}
											disabled={!searchValue.trim()}
										>
											Search
										</XThemeFilterButton>
									</Space>
								</Col>
							</Row>
						</XThemeFilterCards>
						<XThemeTable
							columns={columns}
							dataSource={getLogsState.data?.logs || []}
							pagination={{
								total: pagination.total,
								current: pagination.current,
								pageSize: pagination.pageSize,
								showSizeChanger: true,
								pageSizeOptions: ['10', '20', '50', '100'],
								onChange: (page, pageSize) => {
									setPagination((prev) => ({
										...prev,
										current: page,
										pageSize: pageSize,
									}));
									fetchLogs({
										current: page,
										pageSize: pageSize,
									});
								},
							}}
							onChange={handleTableChange}
							loading={getLogsState.apiState === 'loading'}
							rowKey="_id"
							className="customHorizontalScroll"
						/>
					</TabPane>
					<TabPane key="analysis" tab="Analysis Logs">
						<XThemeFilterCards className="smallTopBottomPadding mb-3">
							<Row
								gutter={[15, 15]}
								className="align-items-center"
							>
								<Col
									xs={24}
									sm={16}
									md={12}
									lg={12}
									className="mb-3 mb-md-0"
								>
									<Space>
										<XThemeInputText
											size="large"
											allowClear
											placeholder={`Search by ${
												activeTab === 'case'
													? 'Case ID'
													: 'Analysis ID'
											}`}
											value={searchValue}
											onChange={(e) => {
												setSearchValue(e.target.value);
												if (!e.target.value) {
													handleSearchClear();
												}
											}}
											onPressEnter={() =>
												handleSearch(searchValue)
											}
										/>
										<XThemeFilterButton
											onClick={() =>
												handleSearch(searchValue)
											}
											disabled={!searchValue.trim()}
										>
											Search
										</XThemeFilterButton>
									</Space>
								</Col>
							</Row>
						</XThemeFilterCards>
						<XThemeTable
							columns={columns}
							dataSource={getLogsState.data?.logs || []}
							pagination={{
								total: pagination.total,
								current: pagination.current,
								pageSize: pagination.pageSize,
								showSizeChanger: true,
								pageSizeOptions: ['10', '20', '50', '100'],
								onChange: (page, pageSize) => {
									setPagination((prev) => ({
										...prev,
										current: page,
										pageSize: pageSize,
									}));
									fetchLogs({
										current: page,
										pageSize: pageSize,
									});
								},
							}}
							onChange={handleTableChange}
							loading={getLogsState.apiState === 'loading'}
							rowKey="_id"
							className="customHorizontalScroll"
						/>
					</TabPane>
				</XCustomTabsFilter>
			</XThemeContainer>
		</>
	);
};

const mapStateToProps = (state) => ({
	getLogsState: state.getLogs,
});

const mapDispatchToProps = (dispatch) => ({
	getLogs: (params) => dispatch(getLogsApi(params)),
	getLogsReset: () => dispatch(getLogsReset()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ActivityLogs);
